import {zodResolver} from '@hookform/resolvers/zod';
import {Modal, MultiSelect, Textarea, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {LoadingOverlay} from 'src/design/atoms/LoadingOverlay';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {
  IconAlertTriangle,
  IconExclamationCircle,
  IconInfoCircle,
  IconPrinter,
} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import type {
  BadgeTemplateRequest,
  BadgeTemplateResponse,
} from 'src/api/ProactApiClient';
import {UserTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {
  useCreateBadgeTemplateMutation,
  useGetBadgeTemplateQuery,
  useUpdateBadgeTemplateMutation,
} from 'src/redux/queries/badgeTemplates';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {printBadgeArtifact} from 'src/shared/utils/printBadgeArtifact';
import {useBadgeTemplatePreview} from '../hooks/useBadgeTemplatePreview';
import {BadgeTemplatePreviewFrame} from './BadgeTemplatePreviewFrame';
import {BadgeTemplateTokenPanel} from './BadgeTemplateTokenPanel';

// Mirrors BadgeTemplateValidationRules.MaxHtmlContentBytes (backend).
const MAX_HTML_BYTES = 1024 * 1024;

const USER_TYPE_OPTIONS = [
  {value: String(UserTypeEnum.Employee), label: 'Employee'},
  {value: String(UserTypeEnum.Contractor), label: 'Contractor'},
];

const badgeTemplateFormSchema = z.object({
  name: z.string().trim().min(1, 'Template name is required'),
  description: z.string().optional(),
  userTypes: z.array(z.string()),
  htmlContent: z.string().trim().min(1, 'HTML content is required'),
});

type BadgeTemplateFormValues = z.infer<typeof badgeTemplateFormSchema>;

const emptyValues: BadgeTemplateFormValues = {
  name: '',
  description: '',
  userTypes: [],
  htmlContent: '',
};

const byteLength = (value: string): number =>
  new TextEncoder().encode(value ?? '').length;

export interface BadgeTemplateEditorModalProps {
  opened: boolean;
  /** Target instance whose template is being managed. */
  instanceId: number;
  /** Undefined = create mode. Defined = editing (saving publishes a new version). */
  templateId?: number;
  onClose: () => void;
  onSaved: (saved: BadgeTemplateResponse) => void;
}

export const BadgeTemplateEditorModal: React.FC<
  BadgeTemplateEditorModalProps
> = ({opened, instanceId, templateId, onClose, onSaved}) => {
  const {setAlert} = useAlert();
  const isEditMode = templateId !== undefined;
  const [submitError, setSubmitError] = useState<string | undefined>();

  const {data: existing, isFetching: isLoadingExisting} =
    useGetBadgeTemplateQuery(
      {id: templateId as number, instanceId},
      {skip: !opened || templateId === undefined}
    );

  const [createBadgeTemplate, {isLoading: isCreating}] =
    useCreateBadgeTemplateMutation();
  const [updateBadgeTemplate, {isLoading: isUpdating}] =
    useUpdateBadgeTemplateMutation();
  const isSaving = isCreating || isUpdating;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {errors},
  } = useForm<BadgeTemplateFormValues>({
    defaultValues: emptyValues,
    resolver: zodResolver(badgeTemplateFormSchema),
  });

  useEffect(() => {
    if (!opened) return;
    setSubmitError(undefined);

    if (!isEditMode) {
      reset(emptyValues);
      return;
    }

    if (existing) {
      reset({
        name: existing.name,
        description: existing.description ?? '',
        userTypes: (existing.targetingMetadata?.userTypes ?? []).map(String),
        htmlContent: existing.htmlContent,
      });
    }
  }, [opened, isEditMode, existing, reset]);

  const htmlContent = watch('htmlContent');
  const preview = useBadgeTemplatePreview(htmlContent);

  const combinedWarnings = useMemo(
    () =>
      Array.from(
        new Set([...preview.validationWarnings, ...preview.mergeWarnings])
      ),
    [preview.validationWarnings, preview.mergeWarnings]
  );

  const hasBlockingErrors = preview.validationErrors.length > 0;
  const isLoadingContent = isEditMode && (isLoadingExisting || !existing);
  const disableSave = hasBlockingErrors || isSaving || isLoadingContent;
  const disableTestPrint =
    !preview.html || preview.isPreviewLoading || hasBlockingErrors;

  const handleTestPrint = () => {
    if (!preview.html) return;
    printBadgeArtifact(preview.html);
  };

  const handleClose = () => {
    setSubmitError(undefined);
    onClose();
  };

  const onSubmit = async (values: BadgeTemplateFormValues) => {
    setSubmitError(undefined);

    const model: BadgeTemplateRequest = {
      id: isEditMode ? templateId : undefined,
      instanceId,
      version: isEditMode ? existing?.version : undefined,
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      htmlContent: values.htmlContent,
      targetingMetadata: {
        userTypes: values.userTypes.map(value => Number(value) as UserTypeEnum),
      },
    };

    try {
      const saved = isEditMode
        ? await updateBadgeTemplate(model).unwrap()
        : await createBadgeTemplate(model).unwrap();

      setAlert(
        isEditMode
          ? `"${saved.name}" was published as version ${saved.version}`
          : `"${saved.name}" was successfully created`,
        'success'
      );
      onSaved(saved);
    } catch (error) {
      const message = parseErrorMessage(error);
      setSubmitError(message);
      setAlert(message, 'error');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? `Edit "${existing?.name ?? '…'}"` : 'New Badge Template'}
      size="95%"
      styles={{title: {fontWeight: 700}}}
      data-testid="badge-template-editor-modal">
      <Box pos="relative">
        <LoadingOverlay visible={isLoadingContent} />

        <Stack gap="md">
          {isEditMode && existing && (
            <Alert
              color="blue"
              variant="light"
              icon={<IconInfoCircle size={16} />}
              data-testid="badge-template-versioning-notice">
              Publishing will create version {existing.version + 1} of this
              template. Version {existing.version} remains in the version
              history.
            </Alert>
          )}

          {submitError && (
            <Alert
              color="red"
              variant="light"
              title="Could not save template"
              icon={<IconExclamationCircle size={16} />}
              data-testid="badge-template-submit-error">
              {submitError}
            </Alert>
          )}

          <Grid gutter="lg">
            <Grid.Col span={{base: 12, md: 6}}>
              <Stack gap="md">
                <Controller
                  name="name"
                  control={control}
                  render={({field}) => (
                    <TextInput
                      label="Name"
                      placeholder="e.g. Employee Badge"
                      required
                      error={errors.name?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({field}) => (
                    <Textarea
                      label="Description"
                      placeholder="Optional description of this template"
                      minRows={2}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="userTypes"
                  control={control}
                  render={({field}) => (
                    <MultiSelect
                      label="Targeted user types"
                      description="Leave empty to target any user type"
                      placeholder="Any user type"
                      data={USER_TYPE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <BadgeTemplateTokenPanel />

                <Controller
                  name="htmlContent"
                  control={control}
                  render={({field}) => (
                    <Box>
                      <Textarea
                        label="HTML content"
                        placeholder="Paste or write the badge .print-sheet HTML here, using {{Tokens}} for dynamic values"
                        required
                        autosize
                        minRows={16}
                        maxRows={28}
                        error={errors.htmlContent?.message}
                        styles={{
                          input: {
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            fontSize: 12,
                          },
                        }}
                        {...field}
                      />
                      <Group justify="flex-end" mt={4}>
                        <Text
                          size="xs"
                          c={
                            byteLength(field.value) > MAX_HTML_BYTES
                              ? 'red'
                              : 'dimmed'
                          }>
                          {byteLength(field.value).toLocaleString()} /{' '}
                          {MAX_HTML_BYTES.toLocaleString()} bytes
                        </Text>
                      </Group>
                    </Box>
                  )}
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={{base: 12, md: 6}}>
              <Stack gap="md">
                {hasBlockingErrors && (
                  <Alert
                    color="red"
                    variant="light"
                    title="Fix before publishing"
                    icon={<IconExclamationCircle size={16} />}
                    data-testid="badge-template-validation-errors">
                    <List size="sm">
                      {preview.validationErrors.map((error, index) => (
                        <List.Item key={index}>{error}</List.Item>
                      ))}
                    </List>
                  </Alert>
                )}

                {combinedWarnings.length > 0 && (
                  <Alert
                    color="yellow"
                    variant="light"
                    title="Warnings"
                    icon={<IconAlertTriangle size={16} />}
                    data-testid="badge-template-validation-warnings">
                    <List size="sm">
                      {combinedWarnings.map((warning, index) => (
                        <List.Item key={index}>{warning}</List.Item>
                      ))}
                    </List>
                  </Alert>
                )}

                <BadgeTemplatePreviewFrame
                  html={preview.html}
                  isLoading={preview.isPreviewLoading && !preview.html}
                  emptyMessage="Start typing HTML content to see a live preview."
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Group justify="space-between" mt="md">
            <Group gap="xs">
              <Tooltip
                label="Prints the preview using sample data, so you can check the real printer output before publishing."
                multiline
                w={260}>
                <Button
                  variant="default"
                  leftSection={<IconPrinter size={16} />}
                  onClick={handleTestPrint}
                  disabled={disableTestPrint}
                  data-testid="badge-template-test-print-button">
                  Test Print
                </Button>
              </Tooltip>
              <Text size="xs" c="dimmed">
                Print at 100% / Actual Size — do not use Fit to Page.
              </Text>
            </Group>

            <Group>
              <Button variant="subtle" color="black" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="filled"
                color="yellow"
                onClick={handleSubmit(onSubmit)}
                loading={isSaving}
                disabled={disableSave}
                data-testid="badge-template-save-button">
                {isEditMode
                  ? `Publish Version ${(existing?.version ?? 0) + 1}`
                  : 'Create Template'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Box>
    </Modal>
  );
};
